import type { NowState, NowSuggestion } from './types';
import { jsonHeaders, readApiErrorMessage } from './apiHelpers';

/** A failed Now request. `status` 422 on a reorder means the client's list is stale. */
export class NowRequestError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'NowRequestError';
    this.status = status;
  }
}

async function parseNow(response: Response): Promise<NowState> {
  if (!response.ok) {
    throw new NowRequestError(await readApiErrorMessage(response), response.status);
  }
  return response.json();
}

export async function fetchNow(): Promise<NowState> {
  const response = await fetch('/now', {
    headers: jsonHeaders(),
  });
  return parseNow(response);
}

export async function addNowItem(taskId: string): Promise<NowState> {
  const response = await fetch('/now/items', {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ task_id: taskId }),
  });
  return parseNow(response);
}

export async function removeNowItem(taskId: string): Promise<NowState> {
  const response = await fetch(`/now/items/${taskId}`, {
    method: 'DELETE',
    headers: jsonHeaders(),
  });
  return parseNow(response);
}

/** Sends every active item's task id in the new order; the first is Up next. */
export async function reorderNowItems(taskIds: string[]): Promise<NowState> {
  const response = await fetch('/now/items/order', {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify({ task_ids: taskIds }),
  });
  return parseNow(response);
}

export async function updateNowPreferences(prefs: {
  capacity?: number;
  refillSameDay?: boolean;
}): Promise<NowState> {
  const response = await fetch('/now', {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify({
      capacity: prefs.capacity,
      refill_same_day: prefs.refillSameDay,
    }),
  });
  return parseNow(response);
}

export async function fetchNowSuggestions(): Promise<NowSuggestion[]> {
  const response = await fetch('/now/suggestions', {
    headers: jsonHeaders(),
  });
  if (!response.ok) throw new Error(await readApiErrorMessage(response));
  const data = await response.json();
  return data.suggestions || [];
}
